// #1399 — Mehrfachauswahl in der Inbox (Web/Windows-Hülle).
// Reine Helfer, damit Zustand „alle / einige / keine“ und das Aufräumen der
// Auswahl nach dem Neuladen ohne DOM testbar sind.

import type { InboxItem, InboxItemState } from "./api";

/** Stabiler Schlüssel eines Eintrags (Quelle + Id sind zusammen eindeutig). */
export function inboxKey(item: Pick<InboxItem, "source_type" | "source_id">): string {
  return `${item.source_type}-${item.source_id}`;
}

export type SelectAllState = "none" | "some" | "all";

/** Zustand des Kästchens „Alle auswählen“ bezogen auf die SICHTBAREN Einträge. */
export function selectAllState(visible: readonly InboxItem[], selected: ReadonlySet<string>): SelectAllState {
  if (visible.length === 0) return "none";
  let n = 0;
  for (const i of visible) if (selected.has(inboxKey(i))) n++;
  if (n === 0) return "none";
  return n === visible.length ? "all" : "some";
}

/** Klick auf „Alle auswählen“: bei „alle“ leeren, sonst alle sichtbaren wählen. */
export function toggleAll(visible: readonly InboxItem[], selected: ReadonlySet<string>): Set<string> {
  return selectAllState(visible, selected) === "all" ? new Set() : new Set(visible.map(inboxKey));
}

/** Ein Eintrag an/aus. Liefert immer ein neues Set (Signal-Änderung). */
export function toggleOne(selected: ReadonlySet<string>, key: string): Set<string> {
  const next = new Set(selected);
  if (next.has(key)) next.delete(key);
  else next.add(key);
  return next;
}

/** Nur Schlüssel behalten, die noch sichtbar sind (nach Neuladen, Filter, Reiter). */
export function pruneSelection(selected: ReadonlySet<string>, visible: readonly InboxItem[]): Set<string> {
  const keys = new Set(visible.map(inboxKey));
  const next = new Set<string>();
  for (const k of selected) if (keys.has(k)) next.add(k);
  return next;
}

export type InboxBulkAction = "done" | "unread" | "snooze";

/**
 * Sammelaktionen je Reiter. „Ungelesen“ im Reiter „Erledigt“ gilt nicht für
 * Einträge, die nur wegen der erledigten AUFGABE erledigt sind (`task_done`):
 * der Zustand hängt dort an der Aufgabe, siehe Inbox.tsx.
 */
export function bulkActionsFor(tab: InboxItemState): InboxBulkAction[] {
  if (tab === "unread") return ["done", "snooze"];
  if (tab === "snoozed") return ["done", "unread"];
  return ["unread"];
}

export function bulkTargets(items: readonly InboxItem[], action: InboxBulkAction): InboxItem[] {
  return action === "unread" ? items.filter((i) => i.task_done !== true) : [...items];
}
